// Utility functions for dashboard grouping and workspace dashboard logic
import { IDashboardGroup } from "models/dashboard";

import {
  Dashboard,
  Workspace,
  WorkspaceDashboard
} from "../../../../../models/workspace";

/**
 * Returns the title, order, and help text for a dashboard type.
 */
export function getDashboardTypeTitle(type: string): [string, number, string] {
  const title: string = type;
  let order = 0;
  let help = "";
  switch (type) {
    case "Personal":
      order = 0;
      help = "Dashboards you have personally created and are only available to you.";
      break;
    case "Shared":
      order = 1;
      help = "Dashboards shared by other users in your organization.";
      break;
    case "Organization":
      order = 2;
      help = "Dashboards created and maintained by your admins.";
      break;
    case "System":
      order = 3;
      help = "Dashboards created by the system that cannot be modified.";
      break;
    default:
      order = 99;
      help = "";
  }
  return [title, order, help];
}

/**
 * Groups dashboards by a key (usually 'type'), returning sorted dashboard groups.
 */
export function groupDashboardsByType(xs: Dashboard[], key: string): IDashboardGroup[] {
  const map = xs.reduce((rv, x) => {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {} as Record<string, Dashboard[]>);

  const groups: IDashboardGroup[] = [];
  for (const [key, dashboards] of Object.entries(map)) {
    const [title, order, help] = getDashboardTypeTitle(key);
    groups.push({
      groupKey: key,
      groupTitle: title,
      order,
      help,
      dashboards: dashboards as Dashboard[]
    });
  }
  return groups.sort((a, b) => (a.order > b.order ? 1 : b.order > a.order ? -1 : 0));
}

/**
 * Returns dashboards that are available to be added to a workspace.
 * Excludes dashboards already in the workspace and, for non-personal workspaces, excludes personal dashboards.
 */
export function getAvailableDashboards(
  dashboards: Dashboard[],
  workspace: Workspace | undefined
): Dashboard[] {
  const workspaceDashboards = (workspace?.dashboards || []).filter(
    Boolean
  ) as WorkspaceDashboard[];
  return dashboards.filter(
    (d) =>
      !workspaceDashboards.some((wd) => wd.dashboardId === d.dashboardId) &&
      (workspace?.type === "Personal" || d.type !== "Personal")
  );
}

/**
 * Returns the list of dashboard objects that are currently active in the workspace.
 */
export function getActiveDashboardList(
  workspace: Workspace | undefined,
  dashboards: Dashboard[]
): Dashboard[] {
  const workspaceDashboards = (workspace?.dashboards || []).filter(
    Boolean
  ) as WorkspaceDashboard[];
  return workspaceDashboards.reduce<Dashboard[]>((list, d) => {
    const match = dashboards.find((dd) => dd.dashboardId === d.dashboardId);
    if (match) {
      list.push(match);
    }
    return list;
  }, []);
}
